import { useEffect, useState } from 'react';

import { faBoxOpen } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';

import { Scenario } from 'components/types';

import { getScenariosForPlanUnits, updateScenario } from 'RestAPIs/scenario';

import { planUnitsAtom } from '../../atoms';

import {
  Button,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  Tooltip
} from '@mui/material';

import {
  Dialog,
  FontAwesomeSvgIcon,
  LoadingOverlay,
  showSnackbar
} from 'components/shared';
import { TableHeaderRow, ZebraStripedTableRow } from 'components/shared/Table';

type Props = {
  isOpen: boolean;
  handleClose: () => void;
};

const ScenarioArchiveDialog = ({ isOpen, handleClose }: Props) => {
  const { t } = useTranslation('common');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const planUnits = useRecoilValue(planUnitsAtom);

  const loadScenarios = async () => {
    const { data } = await getScenariosForPlanUnits({
      planUnitIds: planUnits.map(({ id }) => id),
      options: { isArchived: true }
    });
    setScenarios(data);
  };

  useEffect(() => {
    loadScenarios();
  }, []);

  const onClickDeArchiveScenario = async (scenario: Scenario) => {
    setIsLoading(true);

    try {
      await updateScenario(
        {
          ...scenario,
          archived: false
        },
        scenario.scenarioId
      );

      showSnackbar({
        severity: 'success',
        message: t('scenario.saved')
      });
      loadScenarios();
      setIsLoading(false);
    } catch {
      showSnackbar({
        severity: 'error',
        message: t('somethingWentWrong')
      });
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      handleClose={handleClose}
      open={isOpen}
      title={t('scenario.archive')}
    >
      <Table size="small">
        <TableHead>
          <TableHeaderRow>
            <TableCell>{t('scenario.name')}</TableCell>
            <TableCell>{t('scenario.year')}</TableCell>
            <TableCell>{t('scenario.planUnits')}</TableCell>
            <TableCell />
          </TableHeaderRow>
        </TableHead>
        <TableBody>
          {scenarios.map((scenario) => (
            <ZebraStripedTableRow key={scenario.scenarioId}>
              <TableCell>{scenario.name}</TableCell>
              <TableCell>{scenario.year}</TableCell>
              <TableCell>
                {scenario.costCenters
                  .map(({ nameShort }) => nameShort)
                  .join(', ')}
              </TableCell>
              <TableCell>
                <Tooltip title={t('scenario.deArchive') || ''} arrow>
                  <IconButton
                    size="small"
                    onClick={() => onClickDeArchiveScenario(scenario)}
                  >
                    <FontAwesomeSvgIcon icon={faBoxOpen} fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </ZebraStripedTableRow>
          ))}
        </TableBody>
      </Table>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={handleClose}>
          {t('close')}
        </Button>
      </DialogActions>
      <LoadingOverlay loading={isLoading} />
    </Dialog>
  );
};

export default ScenarioArchiveDialog;
